"use client";

import { Suspense, useEffect, useState } from "react";
import PlacesDesktopScreen from "./_desktop";
import PlacesMobileScreen from "./_mobile";
import { buildPlaceDetailPath } from "@/utils/url-policy.mjs";

const getPlaces = (initialPlacesView) =>
  Array.isArray(initialPlacesView?.places) ? initialPlacesView.places : [];

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const getReviewCount = (place) =>
  Math.max(
    toNumber(place?.totalReviews),
    toNumber(place?.totalDbReviews),
    toNumber(place?.numberOfVisitorReviews),
    toNumber(place?.numberOfBlogReviews)
  );

const getPlaySignal = (place) =>
  Math.max(
    toNumber(place?.hotpleStats?.videoCount),
    toNumber(place?.hotpleStats?.effectiveVideoCount),
    toNumber(place?.hotpleStats?.recentVideoCount)
  );

const getPlaceCategory = (place) =>
  [place?.type, place?.subcategory].filter(Boolean).join(" · ");

const getPlaceSummary = (place, searchQuery) => {
  const parts = [
    getPlaceCategory(place),
    place?.address || place?.old_address,
  ].filter(Boolean);
  if (!parts.length) return searchQuery || "핫플 추천 장소";
  return parts.join(" · ");
};

const getPlaceKey = (place, fallback) =>
  place?.id || place?._id || place?.publicId || place?.placeUniqueNumber || fallback;

const InitialPlacesFallback = ({ initialPlacesView }) => {
  const places = getPlaces(initialPlacesView).slice(0, 12);
  const searchQuery = initialPlacesView?.searchQuery || "지역 핫플";

  if (!places.length) {
    return <div className="min-h-[100dvh] bg-white dark:bg-custom_black" />;
  }

  return (
    <section
      className="flex h-[100dvh] min-h-[620px] w-full overflow-hidden bg-white text-light_primary_label dark:bg-custom_black dark:text-dark_primary_label"
      aria-label={`${searchQuery} 장소 목록`}
    >
      <aside className="flex h-full w-full max-w-[430px] shrink-0 flex-col border-r border-black/10 bg-white dark:border-white/10 dark:bg-custom_black max-md:max-w-none">
        <header className="border-b border-black/10 px-4 py-4 dark:border-white/10">
          <div className="flex h-10 items-center gap-2 rounded-md border border-black/10 bg-light_bg_grey px-3 text-sm text-black/55 dark:border-white/10 dark:bg-dark_bg_grey dark:text-white/60">
            <span aria-hidden="true">⌕</span>
            <span className="font-medium text-black dark:text-white">{searchQuery}</span>
          </div>
          <h1 className="mt-4 text-xl font-bold leading-tight tracking-normal">
            {searchQuery}
          </h1>
          <p className="mt-2 text-xs leading-relaxed text-black/60 dark:text-white/65">
            {initialPlacesView?.rankingDescription ||
              "Hotple Play 반응과 장소 정보를 기준으로 정렬한 추천 장소입니다."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-md border border-black/10 bg-black/[0.02] px-2 py-1 font-semibold text-black/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
              최근 {places[0]?.hotpleStats?.recentWindowDays || 90}일 기준
            </span>
            <span className="rounded-md border border-black/10 px-2 py-1 text-black/60 dark:border-white/10 dark:text-white/65">
              추천 장소 {initialPlacesView?.totalPlaces || places.length}곳
            </span>
          </div>
        </header>

        <ol className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {places.map((place, index) => {
            const href = buildPlaceDetailPath(place);
            const reviewCount = getReviewCount(place);
            const playSignal = getPlaySignal(place);

            return (
              <li
                className="mb-2 rounded-md border border-black/10 bg-white px-3 py-3 dark:border-white/10 dark:bg-custom_black"
                key={getPlaceKey(place, href)}
              >
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-light_bg_grey text-xs font-bold text-black/50 dark:bg-dark_bg_grey dark:text-white/60">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <a
                      className="block break-words text-[15px] font-bold leading-snug hover:text-red"
                      href={href}
                    >
                      {place?.name || "Hotple 장소"}
                    </a>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-black/55 dark:text-white/60">
                      {getPlaceSummary(place, searchQuery)}
                    </p>
                    <p className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold text-black/55 dark:text-white/60">
                      {reviewCount > 0 ? (
                        <span className="rounded border border-black/10 px-1.5 py-0.5 dark:border-white/10">
                          리뷰 {reviewCount}
                        </span>
                      ) : null}
                      {playSignal > 0 ? (
                        <span className="rounded border border-black/10 bg-black/[0.02] px-1.5 py-0.5 text-black/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
                          Play {playSignal}
                        </span>
                      ) : null}
                      <span className="rounded border border-black/10 px-1.5 py-0.5 dark:border-white/10">
                        {searchQuery}
                      </span>
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </aside>

      <div className="relative hidden flex-1 overflow-hidden bg-[#eef1e9] md:block">
        <div
          className="absolute inset-0 opacity-80"
          style={{
            background:
              "linear-gradient(25deg, transparent 0 36%, #f1d98e 36% 37%, transparent 37% 100%), linear-gradient(95deg, transparent 0 52%, #f1d98e 52% 53%, transparent 53% 100%), radial-gradient(ellipse at 65% 60%, #d9ead4 0 9%, transparent 9.5%)",
          }}
        />
        <div className="absolute left-[34%] top-[38%] flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-red text-xs font-bold text-white shadow">
          1
        </div>
        <div className="absolute left-[50%] top-[28%] flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-red text-xs font-bold text-white shadow">
          2
        </div>
        <div className="absolute left-[62%] top-[48%] flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-red text-xs font-bold text-white shadow">
          3
        </div>
      </div>
    </section>
  );
};

const Places = ({ initialPlacesView = null }) => {
  const [isWideScreen, setIsWideScreen] = useState(undefined);

  useEffect(() => {
    document.documentElement.classList.add("hotple-places-hydrated");
    const handleResize = () => setIsWideScreen(window.innerWidth >= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.documentElement.classList.remove("hotple-places-hydrated");
    };
  }, []);

  if (isWideScreen === undefined) {
    return <InitialPlacesFallback initialPlacesView={initialPlacesView} />;
  }

  return (
    <div className="block">
      {isWideScreen ? (
        <PlacesDesktopScreen initialPlacesView={initialPlacesView} />
      ) : (
        <PlacesMobileScreen initialPlacesView={initialPlacesView} />
      )}
    </div>
  );
};

const PlacesWrapper = (props) => {
  return (
    <Suspense fallback={<></>}>
      <Places {...props} />
    </Suspense>
  );
};

export default PlacesWrapper;
